'use client';

import { motion, useReducedMotion } from 'framer-motion';

/**
 * Hero visual para el slide "Editorial" del home carousel.
 *
 * Metáfora: **revista** — una página que gira sobre la bisagra izquierda,
 * revelando el siguiente spread. Complementa la animación de carta-flip del
 * slide Tarot (naipe · bisagra central) con una mecánica editorial distinta
 * (página · bisagra izquierda).
 *
 * La revista RESPONDE al theme: en light mode es papel pergamino con texto
 * noir editorial; en dark mode es papel noir profundo con texto pergamino.
 * En ambos modos el dorado Φ aparece como acento ritual.
 *
 * Respeta `prefers-reduced-motion` (fallback: página en reposo).
 */
export function EditorialHeroAnimation() {
  const reduced = useReducedMotion();

  const turn = reduced ? {} : { rotateY: [0, -155, -155, 0] };

  return (
    <div className="absolute inset-0 overflow-hidden bg-[var(--color-bg)]">
      {/* Halo "luz de lectura" — usa el fg para que se vea claro/oscuro
          según theme con baja intensidad. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 62% 50%, color-mix(in oklch, var(--color-fg) 8%, transparent) 0%, transparent 55%)',
        }}
      />

      {/* Revista centrada en mobile, desplazada a la derecha en desktop para no
          chocar con el overlay tipográfico (eyebrow · title · subtitle · CTA). */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:right-[12%] md:left-auto md:translate-x-0"
        style={{ perspective: '1800px' }}
      >
        <div className="relative h-[440px] w-[310px] md:h-[540px] md:w-[380px]">
          {/* Páginas de fondo — ilusión de libro apilado.
              Jerarquía de tonos: la página más atrás es la más oscura, la
              frontal la más clara. En light mode esto crea el efecto de
              "lomo de libro" con sombras visibles entre las hojas. */}
          <div
            className="absolute top-2 -right-2 h-full w-full rounded-sm border"
            style={{
              backgroundColor: 'var(--color-surface-raised)',
              borderColor: 'var(--color-border-strong)',
              boxShadow: '0 4px 12px color-mix(in oklch, var(--color-fg) 18%, transparent)',
            }}
          />
          <div
            className="absolute top-1 -right-1 h-full w-full rounded-sm border"
            style={{
              backgroundColor: 'var(--color-bg-subtle)',
              borderColor: 'var(--color-border-strong)',
              boxShadow: '0 2px 8px color-mix(in oklch, var(--color-fg) 14%, transparent)',
            }}
          />

          {/* Página principal que gira — es la hoja FRONTAL del libro.
              Es la más clara (en light: `--color-surface` = blanco; en dark:
              `--color-surface` = noir-raised) y tiene la sombra más
              pronunciada para destacar volumen sobre las páginas de atrás. */}
          <motion.div
            className="absolute inset-0 rounded-sm border"
            style={{
              transformOrigin: 'left center',
              transformStyle: 'preserve-3d',
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border-strong)',
              boxShadow:
                '0 20px 50px color-mix(in oklch, var(--color-fg) 22%, transparent), 0 8px 20px color-mix(in oklch, var(--color-fg) 12%, transparent)',
            }}
            initial={{ rotateY: 0 }}
            animate={turn}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: [0.22, 1, 0.36, 1],
              times: [0, 0.42, 0.58, 1],
            }}
          >
            {/* Cara frontal — visible en reposo */}
            <div
              className="absolute inset-0 flex flex-col justify-between p-8 md:p-10"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <div className="flex items-start justify-between">
                <span
                  className="text-[10px] font-bold tracking-[0.35em] uppercase"
                  style={{
                    color: 'var(--color-ritual)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  EDITORIAL · Φ
                </span>
                <span
                  className="text-[10px] tracking-[0.2em] uppercase"
                  style={{
                    color: 'var(--color-fg-subtle)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  XLIX
                </span>
              </div>

              <blockquote className="text-center">
                <p
                  className="font-serif text-[1.85rem] leading-[1.05] italic md:text-[2.1rem]"
                  style={{ color: 'var(--color-fg)' }}
                >
                  Escribimos
                  <br />
                  lo que vestimos.
                </p>
              </blockquote>

              <div className="flex items-end justify-between">
                <span
                  className="text-[10px] tracking-[0.2em] uppercase"
                  style={{
                    color: 'var(--color-fg-subtle)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  ENSAYO Nº VII
                </span>
                <span
                  className="text-[10px] tracking-[0.3em] uppercase"
                  style={{
                    color: 'var(--color-ritual)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  MMXXVI
                </span>
              </div>
            </div>

            {/* Cara posterior — visible al girar */}
            <div
              className="absolute inset-0 flex items-center justify-center p-8 md:p-10"
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <div className="flex flex-col items-center gap-6 md:gap-8">
                <span className="h-px w-20" style={{ backgroundColor: 'var(--color-ritual)' }} />
                <p
                  className="text-center font-serif text-[1.35rem] leading-[1.1] italic md:text-[1.55rem]"
                  style={{ color: 'var(--color-fg)' }}
                >
                  La forma
                  <br />
                  antes de
                  <br />
                  la prenda.
                </p>
                <span className="h-px w-20" style={{ backgroundColor: 'var(--color-ritual)' }} />
                <span
                  className="text-[10px] tracking-[0.3em] uppercase"
                  style={{
                    color: 'var(--color-ritual)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  LOOKBOOK IX
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Gradiente inferior para legibilidad del overlay tipográfico — usa
          --color-bg para fundirse con el fondo del slide. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, color-mix(in oklch, var(--color-bg) 80%, transparent), color-mix(in oklch, var(--color-bg) 10%, transparent), transparent)',
        }}
      />
    </div>
  );
}
